import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';

import CSS from 'csstype';
import { FaCaretDown } from 'react-icons/fa';
import { ColorVariants, OSKThemeType } from '../DefaultThemeProvider';
import { List } from '../List';
import { Box, BoxProps } from '../Box';
import { Text } from '../Typography';
import type { ListEntry } from '../List';

export const SelectionContext = React.createContext({ onClick: (options: any) => {} });

type SelectProps = {
    /** The name of the component */
    name: string;
    /** Maximum width of the select component */
    w?: number;
    /** Children components to be rendered with the option list */
    children?: React.ReactNode;
    /** The default color to render the placeholder/selected text */
    color?: string;
    /** If true, the input will not be changeable */
    disabled?: boolean;
    /** Label text */
    placeholder?: string;
    /** The default selected value */
    defaultValue?: any;
    /** If specified, this will override whatever value is displayed */
    value?: string | ReactNode;
    /** An array of options to populate the list with */
    items: Array<ListEntry>;
    /** If true, the placeholder will be duplicated as an option. When selected, the value will be null */
    allowNull?: boolean;
    /** A method to be invoked when an option is selected */
    onSelect?: (option?: ListEntry) => void;
    /** The style to apply to the main input element */
    style?: CSS.Properties;
    /** The variant theme with which to apply */
    variant?: ColorVariants;
} & Omit<Omit<BoxProps, 'ref'>, 'onSelect'>;

const Select = styled(
    ({
        allowNull,
        children,
        color,
        disabled,
        placeholder,
        name,
        w,
        defaultValue,
        value,
        items,
        onSelect,
        style,
        variant,
        ...props
    }: SelectProps) => {
        const [open, setOpen] = useState<boolean>(false);
        const [selectedItem, setSelectedItem] = useState<any>({ placeholder, value: undefined });
        const theme = useTheme() as OSKThemeType;

        /**
         * This method will handle keyboard events captured while the Select component
         * is open. Such events are used to control focus and monitor for submit/cancel intents.
         *
         * @param evt The keyboard event which is passed from the browser window
         */
        const handleKeyDown = (evt: KeyboardEvent) => {
            evt.preventDefault();
            evt.stopPropagation();
            if (evt.code === 'Escape') {
                setOpen(false);
            }
        };

        /** This hook registers the keydown event handler when the dropdown is open */
        useEffect(() => {
            if (open) window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        });

        /** This method will toggle the popup list */
        const toggleOptions = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(!open);
        };

        /** This hook is the callback method for handling the selection of an element. It saves the selection and closes the popup. */
        const handleSelect = useCallback(
            (option?: ListEntry, silent = false) => {
                if (!option || selectedItem.value !== option.value) {
                    setOpen(false);
                    setSelectedItem(option);
                    if (!silent) {
                        onSelect && onSelect(option);
                    }
                }
            },
            [onSelect, setOpen, setSelectedItem],
        );

        useEffect(() => {
            if (defaultValue) {
                for (const option of items) {
                    if (option.value === defaultValue) {
                        handleSelect(option, true);
                        break;
                    }
                }
            }
        }, [items]);

        const isUnset = (selectedItem.value ?? '') === '';

        return (
            <Box role={'select'} w={w} onClick={toggleOptions} {...props} col>
                {/* This input is a focus-trap. Used to capture tab events.
                NOTE: This trick /may/ introduce unwanted values in form submissions.
                If you see such things, we'll have to rethink this approach.
            */}
                <input
                    style={{ position: 'absolute', top: '-100px' }}
                    onFocus={() => {
                        setOpen(true);
                    }}
                />
                <Box w={w} p={6} style={{ alignItems: 'center', justifyContent: 'center' }} grow>
                    <Box style={style} grow>
                        <Text as="div" color={color ?? theme.colors[variant ?? 'primary'].fg}>
                            {isUnset ? placeholder : value ?? selectedItem.label}
                        </Text>
                    </Box>
                    <Text color={color ?? theme.colors[variant ?? 'primary'].fg}>
                        <FaCaretDown />
                    </Text>
                </Box>
                {!disabled && (
                    <Box style={{ position: 'relative' }}>
                        <Box style={{ position: 'absolute', paddingTop: '4px', zIndex: 2, width: '100%' }}>
                            {open && (
                                <List
                                    variant={variant}
                                    itemStyle={{ width: 'calc(100% - 12px)', padding: '6px' }}
                                    items={items}
                                    onCancel={() => setOpen(false)}
                                    onSelect={handleSelect}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                <input name={name} type="hidden" value={selectedItem.value ?? ''} />
            </Box>
        );
    },
)`
    cursor: pointer;
    background-color: ${(props: any) => props.theme.colors.primary.bg};
`;

export type { SelectProps };
export { Select };
